import React from 'react'
import { ResultDisplayProps } from '../../common'
import { FamilyTreeResult, useFamilyTreeResult } from './hooks'
import { AIResultStatus } from '@zocks-communications/client-sdk-js'
import { NoMeetingResult } from '../../DefaultDisplay/View/NoMeetingResult'
import { FamilyResultView } from './FamilyResultView'
import { useEditingAiResult } from '../../DefaultDisplay/hooks'
import { ListMeetingResult } from '../../DefaultDisplay/View/ListMeetingResult'
import { DefaultMeetingResultEditView } from '../../DefaultDisplay/DefaultMeetingResultEditView'
import { Button } from '../../../../../../../components/Button/Button'
import { ResultOptionsButton } from '../../DefaultDisplay/ResultOptions/ResultOptionsButton'
import { useDefaultResultOptions } from '../../DefaultDisplay/ResultOptions/DefaultResultOptions'
import { AiFeature } from '../../../../../../../models/AiFeature'
import { AiResult } from '../../../../../../../models/AiResult'
import { $getRoot, $insertNodes } from 'lexical'
import { createHeadlessEditor } from '@lexical/headless'
import { LEXICAL_NODES } from '../../Lexical/nodes'
import { $generateNodesFromDOM } from '@lexical/html'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectEditingAiResultIds } from '../../../../../../../store/meetingSummary/display'
import { selectAiResultSaveLoading } from '../../../../../../../store/meetingSummary/aiResults'
import { copyPartialStructuredSummaryResult, HTML_TYPE } from '../../../copy'

export const FamilyTree: React.FC<ResultDisplayProps> = ({ aiFeature, aiResult: _displayedAiResult, showEditButtons }) => {
  const editingAiResultIds = useAppSelector(selectEditingAiResultIds)
  const isResultSaveLoading = useAppSelector(selectAiResultSaveLoading)

  const familyTreeResults = useFamilyTreeResult(_displayedAiResult)
  const {
    aiResult,
    editingAiResult,
    handleResultTextContentChanged,
    handleFormattedResultChanged,
    handleReadOnlyFormattedResultChanged,
    handleCancel,
    handleSave,
  } = useEditingAiResult(aiFeature, _displayedAiResult)

  if (_displayedAiResult.status === AIResultStatus.NO_RESULT) {
    return <NoMeetingResult />
  }

  if (editingAiResultIds.includes(aiResult.id)) {
    return (
      <>
        <div className='default-edit-result-container p-3 mb-4'>
          <DefaultMeetingResultEditView
            aiFeature={aiFeature}
            aiResult={editingAiResult}
            onResultTextContentChange={handleResultTextContentChanged}
            onFormattedResultChange={handleFormattedResultChanged}
          />
        </div>
        {showEditButtons ? (
          <div className='d-flex gap-3 justify-content-end'>
            <Button variant='secondary' className='button-small' onClick={handleCancel} disabled={isResultSaveLoading}>
              Cancel
            </Button>
            <Button variant='primary' className='button-small' onClick={handleSave} disabled={isResultSaveLoading}>
              Save
            </Button>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <div>
      <div className='d-flex flex-column gap-2'>
        {familyTreeResults.map((familyResult, idx) => {
          return (
            <FamilyTreeElement
              aiFeature={aiFeature}
              aiResult={aiResult}
              key={`${familyResult.relationship}-${familyResult.name}`}
              familyTreeResult={familyResult}
              idx={idx}
              handleFormattedResultChanged={handleReadOnlyFormattedResultChanged}
            />
          )
        })}
      </div>
    </div>
  )
}

interface FamilyTreeElementProps {
  aiFeature: AiFeature
  aiResult: AiResult
  familyTreeResult: FamilyTreeResult
  handleFormattedResultChanged: (basePath: string, path: string, contentType: string, content: string) => void
  idx: number
}

const FamilyTreeElement: React.FC<FamilyTreeElementProps> = ({ aiFeature, aiResult, familyTreeResult, idx, handleFormattedResultChanged }) => {
  const path = `$.result[${idx}]`

  const resultHTML = copyPartialStructuredSummaryResult(aiResult, path)[HTML_TYPE]
  const editor = createHeadlessEditor({
    nodes: LEXICAL_NODES,
  })
  editor.update(() => {
    const parser = new DOMParser()
    const dom = parser.parseFromString(resultHTML, HTML_TYPE)
    const nodes = $generateNodesFromDOM(editor, dom)
    $getRoot().select()
    $insertNodes(nodes)
  })

  const resultOptions = useDefaultResultOptions(editor, aiFeature, aiResult, path)
  return (
    <div className='family-result-opened-container-wrapper d-flex gap-2'>
      <div className='family-tree-element-result-options-button'>
        <ResultOptionsButton resultOptions={resultOptions} />
      </div>
      <div className='d-flex gap-2 family-result-opened-container'>
        <FamilyResultView familyResult={familyTreeResult} className='zocks-bg-color-gray-100 border-0 align-self-start' />
        <div className='flex-grow-1'>
          <ListMeetingResult
            aiResult={aiResult}
            aiFeature={aiFeature}
            onFormattedResultChange={handleFormattedResultChanged}
            basePath={'$'}
            path={`$.result[${idx}]['Information']`}
          />
        </div>
      </div>
    </div>
  )
}
